import { useEffect, useRef, useState } from "react"

/**
 * Options for configuring the Intersection Observer behavior
 */
interface UseIntersectionObserverOptions {
  /**
   * Threshold for triggering intersection (0-1).
   * 0.5 means 50% of the element must be visible.
   * @default 0.5
   */
  threshold?: number
  /**
   * Margin around the root viewport for early/late triggering.
   * Accepts CSS margin values like "10px", "10px 20px", etc.
   * @default "0px"
   */
  rootMargin?: string
  /**
   * Whether to trigger only once when element first becomes visible,
   * or continuously track visibility changes.
   * @default true
   */
  triggerOnce?: boolean
}

/**
 * Custom hook for detecting when an element enters or exits the viewport using Intersection Observer API.
 *
 * This hook is optimized for performance and provides a clean interface for tracking element visibility,
 * making it ideal for analytics tracking, lazy loading, animations, and other scroll-based interactions.
 *
 * @template T - The type of DOM element being observed (extends Element)
 * @param options - Configuration options for the Intersection Observer
 *
 * @returns An object containing:
 * - `elementRef`: React ref to attach to the target DOM element
 * - `isIntersecting`: Boolean indicating if element is currently visible
 * - `hasTriggered`: Boolean indicating if element has been visible at least once
 *
 * @example
 * ```tsx
 * // Basic usage for analytics tracking
 * const { elementRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
 *   threshold: 0.5,
 *   triggerOnce: true
 * })
 *
 * useEffect(() => {
 *   if (isIntersecting) {
 *     trackAnalyticEvent('SECTION_VIEWED')
 *   }
 * }, [isIntersecting])
 *
 * return <div ref={elementRef}>Content to track</div>
 * ```
 *
 * @example
 * ```tsx
 * // Lazy loading with early trigger
 * const { elementRef, isIntersecting } = useIntersectionObserver<HTMLImageElement>({
 *   rootMargin: "100px", // Load 100px before entering viewport
 *   threshold: 0.1,      // Trigger when 10% visible
 *   triggerOnce: true
 * })
 *
 * return (
 *   <img
 *     ref={elementRef}
 *     src={isIntersecting ? actualSrc : placeholderSrc}
 *     alt="Lazy loaded image"
 *   />
 * )
 * ```
 *
 * @example
 * ```tsx
 * // Continuous animation tracking
 * const { elementRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
 *   threshold: 0.3,
 *   triggerOnce: false // Track every time it enters/exits
 * })
 *
 * return (
 *   <div
 *     ref={elementRef}
 *     className={`transition-opacity ${isIntersecting ? 'opacity-100' : 'opacity-0'}`}
 *   >
 *     Animated content
 *   </div>
 * )
 * ```
 */
export function useIntersectionObserver<T extends Element>(
  options: UseIntersectionObserverOptions = {}
) {
  const { threshold = 0.5, rootMargin = "0px", triggerOnce = true } = options

  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)
  const elementRef = useRef<T>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // If triggerOnce is true and we've already triggered, don't set up observer
    if (triggerOnce && hasTriggered) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isElementIntersecting = entry.isIntersecting
        setIsIntersecting(isElementIntersecting)

        if (isElementIntersecting && triggerOnce) {
          setHasTriggered(true)
        }
      },
      {
        threshold,
        rootMargin,
      }
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [threshold, rootMargin, triggerOnce, hasTriggered])

  return {
    elementRef,
    isIntersecting,
    hasTriggered,
  }
}
